import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, useEffect, useState } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { BrandProvider } from './brand/brand-provider';

/**
 * lazy() with stale-deploy recovery: after a deploy, an open tab's old
 * index.html can reference hashed chunks that no longer exist — the dynamic
 * import rejects and navigation appears to do nothing. One forced reload per
 * session picks up the fresh index.html.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches React's own LazyExoticComponent constraint
function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (!sessionStorage.getItem('chunk-reloaded')) {
        sessionStorage.setItem('chunk-reloaded', '1');
        window.location.reload();
      }
      throw err;
    }),
  );
}
import { ChatWidgetLoader } from './components/chat-widget-loader';
import { ProtectedRoute } from './components/protected-route';
import { MarketingShell } from './layouts/marketing-shell';
import { setOnUnauthorizedHandler } from './lib/api-client';
import { LandingPage } from './pages/marketing/landing';
import { NotFoundPage } from './pages/marketing/not-found';
import { useAuthStore } from './store/auth-store';
import { ThemeProvider } from './theme/theme-provider';

// Auth pages are lazy so react-hook-form + zod resolvers stay out of the
// entry chunk — the landing page doesn't need them.
const LoginPage = lazyRetry(() =>
  import('./pages/auth/login').then((m) => ({ default: m.LoginPage })),
);
const SignupPage = lazyRetry(() =>
  import('./pages/auth/signup').then((m) => ({ default: m.SignupPage })),
);
const ForgotPasswordPage = lazyRetry(() =>
  import('./pages/auth/forgot-password').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazyRetry(() =>
  import('./pages/auth/reset-password').then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazyRetry(() =>
  import('./pages/auth/verify-email').then((m) => ({ default: m.VerifyEmailPage })),
);

// Code-split the authenticated app — antd + charts live here and they're huge.
// Marketing routes (landing, pricing, auth) stay in the main bundle for SEO
// and a fast first paint.
const AppShell = lazyRetry(() =>
  import('./layouts/app-shell').then((m) => ({ default: m.AppShell })),
);
// antd (ConfigProvider + cssinjs reset) is dashboard-only. Lazy-load it with
// the /app subtree so it never enters the marketing/main bundle — keeps
// marketing routes light and their <head> free of antd's injected styles.
const AntdProvider = lazyRetry(() =>
  import('./theme/antd-provider').then((m) => ({ default: m.AntdProvider })),
);
const ContactPage = lazyRetry(() =>
  import('./pages/marketing/contact').then((m) => ({ default: m.ContactPage })),
);
const PricingPage = lazyRetry(() =>
  import('./pages/marketing/pricing').then((m) => ({ default: m.PricingPage })),
);
const VoicePage = lazyRetry(() =>
  import('./pages/marketing/voice').then((m) => ({ default: m.VoicePage })),
);
const ChatPage = lazyRetry(() =>
  import('./pages/marketing/chat').then((m) => ({ default: m.ChatPage })),
);
const TermsPage = lazyRetry(() =>
  import('./pages/marketing/legal/terms').then((m) => ({ default: m.TermsPage })),
);
const PrivacyPage = lazyRetry(() =>
  import('./pages/marketing/legal/privacy').then((m) => ({ default: m.PrivacyPage })),
);
const AcceptableUsePage = lazyRetry(() =>
  import('./pages/marketing/legal/acceptable-use').then((m) => ({
    default: m.AcceptableUsePage,
  })),
);
const DpaPage = lazyRetry(() =>
  import('./pages/marketing/legal/dpa').then((m) => ({ default: m.DpaPage })),
);
const BotPage = lazyRetry(() =>
  import('./pages/marketing/bot').then((m) => ({ default: m.BotPage })),
);
const SubProcessorsPage = lazyRetry(() =>
  import('./pages/marketing/legal/sub-processors').then((m) => ({
    default: m.SubProcessorsPage,
  })),
);
const CookiesPage = lazyRetry(() =>
  import('./pages/marketing/legal/cookies').then((m) => ({ default: m.CookiesPage })),
);
const DentalIndustryPage = lazyRetry(() =>
  import('./pages/marketing/industries/dental').then((m) => ({ default: m.DentalIndustryPage })),
);
const MedicalIndustryPage = lazyRetry(() =>
  import('./pages/marketing/industries/medical').then((m) => ({ default: m.MedicalIndustryPage })),
);
const LegalIndustryPage = lazyRetry(() =>
  import('./pages/marketing/industries/legal').then((m) => ({ default: m.LegalIndustryPage })),
);
const RealEstateIndustryPage = lazyRetry(() =>
  import('./pages/marketing/industries/real-estate').then((m) => ({ default: m.RealEstateIndustryPage })),
);
const TradesIndustryPage = lazyRetry(() =>
  import('./pages/marketing/industries/trades').then((m) => ({ default: m.TradesIndustryPage })),
);
const SalonsIndustryPage = lazyRetry(() =>
  import('./pages/marketing/industries/salons').then((m) => ({ default: m.SalonsIndustryPage })),
);
const HospitalityIndustryPage = lazyRetry(() =>
  import('./pages/marketing/industries/hospitality').then((m) => ({ default: m.HospitalityIndustryPage })),
);
const ProfessionalServicesIndustryPage = lazyRetry(() =>
  import('./pages/marketing/industries/professional-services').then((m) => ({
    default: m.ProfessionalServicesIndustryPage,
  })),
);
const BlogIndexPage = lazyRetry(() =>
  import('./pages/marketing/blog/index').then((m) => ({ default: m.BlogIndexPage })),
);
const BlogPostPage = lazyRetry(() =>
  import('./pages/marketing/blog/post').then((m) => ({ default: m.BlogPostPage })),
);
const VsAnswerConnectPage = lazyRetry(() =>
  import('./pages/marketing/compare/vs-answerconnect').then((m) => ({ default: m.VsAnswerConnectPage })),
);
const VsRubyPage = lazyRetry(() =>
  import('./pages/marketing/compare/vs-ruby').then((m) => ({ default: m.VsRubyPage })),
);
const AdminPlansPage = lazyRetry(() =>
  import('./pages/app/admin-plans').then((m) => ({ default: m.AdminPlansPage })),
);
const AdminAgentTemplatesPage = lazyRetry(() =>
  import('./pages/app/admin-agent-templates').then((m) => ({ default: m.AdminAgentTemplatesPage })),
);
const AdminClientsPage = lazyRetry(() =>
  import('./pages/app/admin-clients').then((m) => ({ default: m.AdminClientsPage })),
);
const AdminAdminsPage = lazyRetry(() =>
  import('./pages/app/admin-admins').then((m) => ({ default: m.AdminAdminsPage })),
);
const AdminPromotionsPage = lazyRetry(() =>
  import('./pages/app/admin-promotions').then((m) => ({ default: m.AdminPromotionsPage })),
);
const AdminSalesPage = lazyRetry(() =>
  import('./pages/app/admin-sales').then((m) => ({ default: m.AdminSalesPage })),
);
const AgentEditPage = lazyRetry(() =>
  import('./pages/app/agent-edit').then((m) => ({ default: m.AgentEditPage })),
);
const AgentsListPage = lazyRetry(() =>
  import('./pages/app/agents-list').then((m) => ({ default: m.AgentsListPage })),
);
const ApiKeysPage = lazyRetry(() =>
  import('./pages/app/api-keys').then((m) => ({ default: m.ApiKeysPage })),
);
const BillingPage = lazyRetry(() =>
  import('./pages/app/billing').then((m) => ({ default: m.BillingPage })),
);
const BillingUpgradePage = lazyRetry(() =>
  import('./pages/app/billing-upgrade').then((m) => ({
    default: m.BillingUpgradePage,
  })),
);
const CallDetailPage = lazyRetry(() =>
  import('./pages/app/call-detail').then((m) => ({ default: m.CallDetailPage })),
);
const CallsListPage = lazyRetry(() =>
  import('./pages/app/calls-list').then((m) => ({ default: m.CallsListPage })),
);
const LeadsListPage = lazyRetry(() =>
  import('./pages/app/leads-list').then((m) => ({ default: m.LeadsListPage })),
);
const DashboardPage = lazyRetry(() =>
  import('./pages/app/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const PhoneNumbersPage = lazyRetry(() =>
  import('./pages/app/phone-numbers').then((m) => ({ default: m.PhoneNumbersPage })),
);
const AccountPage = lazyRetry(() =>
  import('./pages/app/account').then((m) => ({ default: m.AccountPage })),
);
const AccountDeletePage = lazyRetry(() =>
  import('./pages/marketing/account-delete').then((m) => ({ default: m.AccountDeletePage })),
);
const TeamPage = lazyRetry(() =>
  import('./pages/app/team').then((m) => ({ default: m.TeamPage })),
);
const OnboardingPage = lazyRetry(() =>
  import('./pages/app/onboarding').then((m) => ({ default: m.OnboardingPage })),
);
const IntegrationsPage = lazyRetry(() =>
  import('./pages/app/integrations').then((m) => ({ default: m.IntegrationsPage })),
);

const DomainsPage = lazyRetry(() =>
  import('./pages/app/domains').then((m) => ({ default: m.DomainsPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

function AuthBoundary({ children }: { children: React.ReactNode }): JSX.Element {
  const clear = useAuthStore((s) => s.clear);
  useEffect(() => {
    setOnUnauthorizedHandler(() => clear());
    return () => setOnUnauthorizedHandler(null);
  }, [clear]);
  return <>{children}</>;
}

function LazyFallback(): JSX.Element {
  // Hold blank for 150ms so fast chunk loads never flash a loading state;
  // slower loads get a content-shaped skeleton instead of bare text.
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 150);
    return () => clearTimeout(t);
  }, []);
  if (!show) return <div className="min-h-[40vh]" />;
  return (
    <div
      className="container flex min-h-[60vh] flex-col gap-4 py-16"
      aria-busy="true"
      aria-label="Loading page"
    >
      <div className="h-8 w-2/5 animate-pulse rounded-md bg-muted" />
      <div className="h-4 w-3/5 animate-pulse rounded-md bg-muted" />
      <div className="h-4 w-1/2 animate-pulse rounded-md bg-muted" />
      <div className="mt-4 h-64 w-full animate-pulse rounded-xl bg-muted/60" />
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="ui-theme">
        <BrandProvider>
            <BrowserRouter>
              <AuthBoundary>
                <Suspense fallback={<LazyFallback />}>
                  <Routes>
                    <Route element={<MarketingShell />}>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/voice" element={<VoicePage />} />
                      <Route path="/chat" element={<ChatPage />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/signup" element={<SignupPage />} />
                      <Route path="/verify-email" element={<VerifyEmailPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      {/* /legal/* URLs were submitted in the SMS toll-free registration (2026-10-21). */}
                      <Route path="/legal/privacy" element={<Navigate to="/privacy" replace />} />
                      <Route path="/legal/terms" element={<Navigate to="/terms" replace />} />
                      <Route path="/account/delete" element={<AccountDeletePage />} />
                      <Route path="/acceptable-use" element={<AcceptableUsePage />} />
                      <Route path="/bot" element={<BotPage />} />
                      <Route path="/dpa" element={<DpaPage />} />
                      <Route path="/sub-processors" element={<SubProcessorsPage />} />
                      <Route path="/cookies" element={<CookiesPage />} />
                      {/* Industry vertical pages */}
                      <Route path="/industries/dental" element={<DentalIndustryPage />} />
                      <Route path="/industries/medical" element={<MedicalIndustryPage />} />
                      <Route path="/industries/legal" element={<LegalIndustryPage />} />
                      <Route path="/industries/real-estate" element={<RealEstateIndustryPage />} />
                      <Route path="/industries/trades" element={<TradesIndustryPage />} />
                      <Route path="/industries/salons" element={<SalonsIndustryPage />} />
                      <Route path="/industries/hospitality" element={<HospitalityIndustryPage />} />
                      <Route path="/industries/professional-services" element={<ProfessionalServicesIndustryPage />} />
                      {/* Blog */}
                      <Route path="/blog" element={<BlogIndexPage />} />
                      <Route path="/blog/:slug" element={<BlogPostPage />} />
                      {/* Comparison pages */}
                      <Route path="/compare/vs-smith-ai" element={<Navigate to="/compare/vs-answerconnect" replace />} />
                      <Route path="/compare/vs-answerconnect" element={<VsAnswerConnectPage />} />
                      <Route path="/compare/vs-ruby" element={<VsRubyPage />} />
                    </Route>
                    <Route
                      path="/app"
                      element={
                        <ProtectedRoute>
                          <AntdProvider>
                            <AppShell />
                          </AntdProvider>
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<DashboardPage />} />
                      <Route path="onboarding" element={<OnboardingPage />} />
                      <Route path="agents" element={<AgentsListPage />} />
                      <Route path="agents/:id" element={<AgentEditPage />} />
                      <Route path="phone-numbers" element={<PhoneNumbersPage />} />
                      <Route path="calls" element={<CallsListPage />} />
                      <Route path="calls/:id" element={<CallDetailPage />} />
                      <Route path="leads" element={<LeadsListPage />} />
                      <Route path="team" element={<TeamPage />} />
                      <Route path="account" element={<AccountPage />} />
                      <Route path="billing" element={<BillingPage />} />
                      <Route path="billing/upgrade" element={<BillingUpgradePage />} />
                      <Route path="api-keys" element={<ApiKeysPage />} />
                      <Route path="integrations" element={<IntegrationsPage />} />
                      <Route path="domains" element={<DomainsPage />} />
                      <Route path="admin/clients" element={<AdminClientsPage />} />
                      <Route path="admin/admins" element={<AdminAdminsPage />} />
                      <Route path="admin/plans" element={<AdminPlansPage />} />
                      <Route path="admin/promotions" element={<AdminPromotionsPage />} />
                      <Route path="admin/sales" element={<AdminSalesPage />} />
                      <Route path="admin/agent-templates" element={<AdminAgentTemplatesPage />} />
                    </Route>
                    {/* Unknown URLs get a real 404 inside the marketing shell
                        instead of silently redirecting home (soft-404). */}
                    <Route element={<MarketingShell />}>
                      <Route path="*" element={<NotFoundPage />} />
                    </Route>
                  </Routes>
                </Suspense>
              </AuthBoundary>
            </BrowserRouter>
          <ChatWidgetLoader />
        </BrandProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
